/**
 * Imports
 * @internal
 * @ignore
 */
import { MenuItem, TextField } from '@material-ui/core'
import AppBar from '@material-ui/core/AppBar'
import Box from '@material-ui/core/Box'
import Hidden from '@material-ui/core/Hidden'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import Toolbar from '@material-ui/core/Toolbar'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import MenuIcon from '@material-ui/icons/Menu'
import { getCompanies, impersonate } from 'actions/user'
import PermissionProvider from 'components/provider/permission'
import { MenuProps } from 'components/textfield'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Connected as ProfileSummary } from '../profile-summary/'
import * as Constants from './../../application/constants'

/**
 * Types
 * @internal
 * @ignore
 */
interface TopBarProps {
  workspaceSelector: ReactElement | undefined
  onMenuToggle: () => void
  showBackButton: boolean
  mobileTitle: string
  disableFilter?: boolean
}

/**
 * Styles
 * @internal
 * @ignore
 */
const useStyles = makeStyles((theme: any) => ({
  topBar: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  topBarShift: {
    width: `calc(100% - ${theme.drawerWidth as string}px)`,
    marginLeft: theme.drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginLeft: 0
    }
  },
  link: {
    height: '100%',
    borderRadius: 0,
    border: 0,
    color: theme.palette.text.primary
  },
  linkSelected: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.primary.light,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.background.contrastText
    }
  },
  logoContainer: {
    width: theme.drawerWidth,
    height: theme.toolbarHeight,
    minHeight: theme.toolbarHeight,
    backgroundColor: theme.palette.background.bg3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  logo: {
    backgroundImage: 'url(/logo.png)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 230,
    width: 230,
    height: 35,
    minHeight: 35
  },
  dropdown: {
    // Width 180 allows 3 dropdowns to fit in the top bar on a medium width screen.
    width: 180
  }
}))

/**
 * Impersonation Dialog
 *
 * @returns ReactElement
 */
const ImpersonateDropdown = (): ReactElement => {
  const [companies, setCompanies] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set())
  const dispatch = useDispatch()
  const container = useRef()
  const companyId = useSelector((state: any) => (state._user.impersonate?.companyId) || state.auth.payload.companyId)
  const classes: any = useStyles()

  useEffect(() => {
    setLoading && getCompanies(setLoading, companyId).then((companies) => {
      const activeCompanies = companies.filter((c) => !c.archived)
      setCompanies(activeCompanies)
      
      // Auto-expand all ancestors of currently selected company
      const currentCompany = activeCompanies.find(c => c.id === companyId)
      if (currentCompany) {
        const ancestorIds = new Set<string>()
        let parent = activeCompanies.find(c => c.id === currentCompany.parentCompanyId)
        while (parent) {
          ancestorIds.add(parent.id)
          parent = activeCompanies.find(c => c.id === parent.parentCompanyId)
        }
        setExpandedCompanies(ancestorIds)
      }
    })
  }, [setCompanies, companyId])

  // Recursively build hierarchical structure
  const buildHierarchy = (parentId: string | null, level: number = 0): any[] => {
    const children = companies.filter(c => c.parentCompanyId === parentId)
    const result: any[] = []
    
    children.forEach(company => {
      const descendants = companies.filter(c => c.parentCompanyId === company.id)
      const hasChildren = descendants.length > 0
      const isExpanded = expandedCompanies.has(company.id)
      
      // Add current company
      result.push({
        ...company,
        level,
        hasChildren,
        childCount: descendants.length,
        isExpanded
      })
      
      // Recursively add children if expanded
      if (hasChildren && isExpanded) {
        const nestedChildren = buildHierarchy(company.id, level + 1)
        result.push(...nestedChildren)
      }
    })
    
    return result
  }

  const getCompanyHierarchy = () => {
    // Find companies that should be treated as roots:
    // 1. Companies with no parentCompanyId
    // 2. Companies whose parent is not in the available companies list
    const availableIds = new Set(companies.map(c => c.id))
    const rootCompanies = companies.filter(c => 
      !c.parentCompanyId || !availableIds.has(c.parentCompanyId)
    )
    
    // Build hierarchy for each root
    const result: any[] = []
    rootCompanies.forEach(root => {
      const descendants = companies.filter(c => c.parentCompanyId === root.id)
      const hasChildren = descendants.length > 0
      const isExpanded = expandedCompanies.has(root.id)
      
      result.push({
        ...root,
        level: 0,
        hasChildren,
        childCount: descendants.length,
        isExpanded
      })
      
      if (hasChildren && isExpanded) {
        const nestedChildren = buildHierarchy(root.id, 1)
        result.push(...nestedChildren)
      }
    })
    
    return result
  }

  const toggleExpand = (companyId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    setExpandedCompanies(prev => {
      const newSet = new Set(prev)
      if (newSet.has(companyId)) {
        newSet.delete(companyId)
      } else {
        newSet.add(companyId)
      }
      return newSet
    })
  }

  const handleChange = (values: any) => {
    const selectedId = values.target.value
    const company = companies.find((i: any) => String(i.id) === String(selectedId))
    // @ts-expect-error
    company && dispatch(impersonate(company.id, company.name, company.companyType), [dispatch])
      // @ts-expect-error
      .then(() => setLoading(false))
  }

  const hierarchicalCompanies = getCompanyHierarchy()

  const selectedCompany = companies.find(c => c.id === companyId)

  return (
    <Box style={{ minWidth: 230, marginRight: 20 }}>
      <TextField
        label="Company"
        disabled={loading || !companies.length}
        name="company"
        variant='filled'
        InputProps={{ classes: { root: classes.selectDropdown } }}
        SelectProps={{
          // @ts-expect-error Bad types have been used here...
          MenuProps: {
            ...MenuProps,
            PopoverClasses: { paper: classes.whiteDropdown },
            container: container.current,
            disablePortal: true,
            transformOrigin: { vertical: 0, horizontal: 'left' }
          },
          renderValue: (value) => {
            const company = companies.find(c => String(c.id) === String(value))
            return company?.name || (loading ? 'Loading...' : '')
          }
        }}
        fullWidth
        select
        value={companyId}
        defaultValue=""
        onChange={handleChange}>
        {hierarchicalCompanies?.map((company, index) => {
          // Calculate indentation based on level
          const indentation = 16 + (company.level * 20)
          const prefix = company.level > 0 ? '└─ ' : ''
          
          return (
            <MenuItem 
              key={`company-${index}`} 
              value={company.id}
              style={{ 
                paddingLeft: indentation,
                fontWeight: company.level === 0 ? 'bold' : 'normal',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span>
                {prefix}
                {company.name}
              </span>
              {company.hasChildren && (
                <span 
                  onClick={(e) => toggleExpand(company.id, e)}
                  style={{ 
                    marginLeft: 8,
                    fontSize: '0.85em',
                    opacity: 0.7,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  {company.isExpanded ? '▼' : '▶'}
                </span>
              )}
            </MenuItem>
          )
        })}
      </TextField>
    </Box>
  )
}

/**
 * Top Bar
 *
 * @param props Element props.
 * @param props.workspaceSelector Workspaces Selector.
 * @param props.showBackButton Show Back Button.
 * @param props.onMenuToggle onMenuToggle.
 * @param props.mobileTitle MobileTitle.
 * @returns ReactElement
 */
const TopBar = ({ workspaceSelector, showBackButton, onMenuToggle, mobileTitle }: TopBarProps): ReactElement => {
  const classes = useStyles()

  const debugMode = useSelector((state: any) => (state._user.debugMode))
  const showCompanies = useSelector((state: any) => (state._user.showCompanies))

  return (
    <React.Fragment>
      <AppBar position="fixed" color="default" className={classes.topBar}>
        <Toolbar disableGutters>
          <Hidden xsDown>
            <Box display="flex" width="100%" height="100%" alignItems="stretch" justifyContent="stretch">
              <Hidden mdUp>
                <Box flex={1} mr={1} width="100%" minWidth={80} maxWidth={80} justifyContent="center" alignItems="center" display="flex">
                  <IconButton onClick={onMenuToggle} size="small">
                    {showBackButton ? <ArrowBackIcon /> : <MenuIcon />}
                  </IconButton>
                </Box>
              </Hidden>
              <Hidden smDown>
                <Box className={classes.logoContainer}>
                  <Box flexGrow={0} className={classes.logo} />
                </Box>
              </Hidden>
              <Box>
                {workspaceSelector}
              </Box>
              <Box flexGrow={1}>
                &nbsp;
              </Box>
              {debugMode &&
                <Box>
                  <PermissionProvider>
                    {({ [Constants.PERMISSION.ERGOSENSE]: ergosense }) => {
                      // Only super-users are allowed to impersonate
                      if (!ergosense) return null

                      return (
                        <ImpersonateDropdown />
                      )
                    }}
                  </PermissionProvider>
                </Box>
              }

              <Box flexShrink={1} mr={1} height="auto">
                <ProfileSummary />
              </Box>
            </Box>
          </Hidden>
          <Hidden smUp>
            <Box display="flex" width="100%" alignItems="center">
              <Box flex={1} mr={1} width="100%" minWidth={60} justifyContent="center" alignItems="center" display="flex">
                <IconButton onClick={onMenuToggle} size="small">
                  {showBackButton ? <ArrowBackIcon /> : <MenuIcon />}
                </IconButton>
              </Box>
              <Box flexGrow={1} width="100%">
                {mobileTitle}
              </Box>
            </Box>
          </Hidden>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  )
}

export default TopBar
